import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HorizontalCarousel } from '@/components/HorizontalCarousel';

const API = process.env.REACT_APP_BACKEND_URL;

// Static taxonomy: how destinations are grouped. City data itself comes from the API.
const CLUSTERS = [
  { id: 'cities',   label: 'Cities',               emoji: '🏙️', tagline: 'Capitals and cultural hubs',        slugs: ['tirana', 'durres', 'shkoder', 'korca', 'vlora', 'pristina', 'prizren', 'peja', 'gjakova', 'skopje', 'bitola', 'tetovo', 'ferizaj'] },
  { id: 'coastal',  label: 'Coastal Destinations',  emoji: '🏖️', tagline: 'Beaches, coves & Riviera escapes', slugs: ['saranda', 'ksamil', 'himare', 'dhermi', 'divjake', 'struga'] },
  { id: 'mountain', label: 'Mountain Destinations', emoji: '⛰️', tagline: 'Alpine valleys, hikes & ski',       slugs: ['theth', 'valbona', 'mavrovo', 'brezovica', 'krusevo'] },
  { id: 'unesco',   label: 'UNESCO Heritage',       emoji: '🏛️', tagline: 'World heritage sites',             slugs: ['berat', 'gjirokastra', 'ohrid'] },
  { id: 'weekend',  label: 'Weekend Escapes',       emoji: '✨', tagline: 'Perfect 1-2 day getaways',          slugs: ['kruja', 'pogradec', 'prizren'] },
];

const niceCountry = (s) => (s || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const ExploreDestinations = () => {
  const [cities, setCities] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/cities`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setCities(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  if (cities.length === 0) return null;
  const bySlug = Object.fromEntries(cities.map((c) => [c.slug, c]));

  return (
    <section className="py-16 bg-white" data-testid="explore-destinations">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#00AA6C] mb-1">
              <Compass className="w-3.5 h-3.5" /> Explore Destinations
            </div>
            <h2 className="font-heading text-3xl font-bold text-gray-900">Pick your Balkan adventure</h2>
            <p className="text-gray-600 mt-1 text-sm">{cities.length} destinations across Albania, Kosovo &amp; North Macedonia</p>
          </div>
        </div>

        <div className="space-y-10">
          {CLUSTERS.map((cluster) => {
            const clusterCities = cluster.slugs.map((s) => bySlug[s]).filter(Boolean);
            if (clusterCities.length === 0) return null;
            return (
              <div key={cluster.id} data-testid={`cluster-${cluster.id}`}>
                <div className="flex items-end justify-between mb-3 gap-4">
                  <div>
                    <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                      <span>{cluster.emoji}</span> {cluster.label}
                    </h3>
                    <p className="text-xs text-gray-500">{cluster.tagline}</p>
                  </div>
                </div>
                <HorizontalCarousel testid={`cluster-carousel-${cluster.id}`}>
                  {clusterCities.map((c) => (
                    <Link
                      key={`${cluster.id}-${c.slug}`}
                      to={`/${c.country}/${c.slug}`}
                      className="group relative rounded-2xl overflow-hidden block w-60 sm:w-64 h-72 shadow hover:shadow-2xl transition-shadow"
                      data-testid={`destination-${c.slug}`}
                    >
                      <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <div className="text-[10px] uppercase tracking-wider text-white/75 mb-0.5">{niceCountry(c.country)}</div>
                        <h4 className="font-heading font-bold text-xl leading-tight">{c.name}</h4>
                        <p className="text-xs text-white/85 mt-1 line-clamp-2">{c.tagline}</p>
                        <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#00AA6C] bg-white px-2 py-0.5 rounded-full">
                          Discover <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </HorizontalCarousel>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Button variant="outline" size="lg" asChild>
            <Link to="/categories">Browse all categories</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
